export const REDACTED = "[redacted]";

const SENSITIVE_HEADERS = new Set([
  "authorization",
  "cookie",
  "set-cookie",
  "x-api-key",
  "x-auth-token",
  "x-refresh-token",
]);

const SENSITIVE_QUERY_PARAMS = new Set([
  "token",
  "access_token",
  "refresh_token",
  "id_token",
  "auth",
  "authorization",
  "code",
  "email",
  "phone",
  "password",
  "secret",
  "api_key",
  "apikey",
]);

const URL_IN_TEXT = /https?:\/\/[^\s)<>"']+/gi;
const BEARER_RE = /\bBearer\s+[A-Za-z0-9\-_.+/=]+/gi;
const JWT_RE = /\b[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g;
const EMAIL_RE = /[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/g;
const E164_RE = /\+\d{7,15}\b/g;

export function scrubHeaders<T extends Record<string, unknown>>(headers: T): T {
  const out: Record<string, unknown> = { ...headers };
  for (const key of Object.keys(out)) {
    if (SENSITIVE_HEADERS.has(key.toLowerCase())) {
      out[key] = REDACTED;
    }
  }
  return out as T;
}

export function scrubUrl(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url, "http://placeholder.local");
  } catch {
    return url;
  }
  let changed = false;
  for (const key of Array.from(parsed.searchParams.keys())) {
    if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
      parsed.searchParams.set(key, REDACTED);
      changed = true;
    }
  }
  if (!changed) return url;
  const out = parsed.toString().replace(/%5Bredacted%5D/gi, REDACTED);
  return out.startsWith("http://placeholder.local")
    ? out.replace("http://placeholder.local", "")
    : out;
}

export function scrubText(text: string): string {
  if (!text) return text;
  let out = text;
  out = out.replace(URL_IN_TEXT, (m) => scrubUrl(m));
  out = out.replace(BEARER_RE, REDACTED);
  out = out.replace(JWT_RE, REDACTED);
  out = out.replace(EMAIL_RE, REDACTED);
  out = out.replace(E164_RE, REDACTED);
  return out;
}

type Breadcrumb = {
  category?: string;
  type?: string;
  message?: string;
  data?: Record<string, unknown> & {
    url?: string;
    headers?: Record<string, unknown>;
  };
};

export function scrubBreadcrumb(breadcrumb: Breadcrumb | null): Breadcrumb | null {
  if (!breadcrumb) return breadcrumb;
  const out: Breadcrumb = { ...breadcrumb };
  if (out.data) {
    const data = { ...out.data };
    if (typeof data.url === "string") {
      data.url = scrubUrl(data.url);
    }
    if (data.headers && typeof data.headers === "object") {
      data.headers = scrubHeaders(data.headers as Record<string, unknown>);
    }
    out.data = data;
  }
  if (typeof out.message === "string") {
    out.message = scrubText(out.message);
  }
  return out;
}

type SentryEventLike = {
  request?: {
    url?: string;
    headers?: Record<string, unknown>;
    query_string?: string | Record<string, unknown>;
  };
  exception?: {
    values?: { value?: string; type?: string }[];
  };
  breadcrumbs?: Breadcrumb[];
  message?: string;
};

export function scrubEvent<T extends SentryEventLike>(event: T): T {
  if (event.request) {
    const req = { ...event.request };
    if (typeof req.url === "string") req.url = scrubUrl(req.url);
    if (req.headers) req.headers = scrubHeaders(req.headers);
    event.request = req;
  }
  if (event.exception?.values) {
    event.exception = {
      ...event.exception,
      values: event.exception.values.map((v) =>
        v.value ? { ...v, value: scrubText(v.value) } : v,
      ),
    };
  }
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs
      .map((b) => scrubBreadcrumb(b))
      .filter((b): b is Breadcrumb => b !== null);
  }
  if (typeof event.message === "string") {
    event.message = scrubText(event.message);
  }
  return event;
}
